/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from 'src/utils/logicTypes';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IIndividualGradesState {
    version: Dtos.Version | null;
    category: Dtos.Category | null;
    term: Dtos.Term | null;
    subterm: Dtos.Term | null;
    grades: Record<number, Dtos.Grade>;
    view: number[];
    loadState: IRequestState;
}

interface IInitialGradesState extends Record<number, IIndividualGradesState> {
}

export interface IGradesState {
   grades: IInitialGradesState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const GradesInitialIndividualState: IIndividualGradesState = {
    grades: {},
    version: null,
    category: null,
    term: null,
    subterm: null,
    view: [],
    loadState: {
        state: RequestState.None
    }
};

const GradesInitialState: IInitialGradesState = {
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useGradesSelector: TypedUseSelectorHook<IGradesState> = useSelector;

export const GradesSelectors = {
    selectGradesVersion: (state: IGradesState, termId: number) => state.grades[termId] ? state.grades[termId].version : GradesInitialIndividualState.version,
    selectGradesCategory: (state: IGradesState, termId: number) => state.grades[termId] ? state.grades[termId].category : GradesInitialIndividualState.category,
    selectGradesTerm: (state: IGradesState, termId: number) => state.grades[termId] ? state.grades[termId].term : GradesInitialIndividualState.term,
    selectGradesSubterm: (state: IGradesState, termId: number) => state.grades[termId] ? state.grades[termId].subterm : GradesInitialIndividualState.subterm,
    selectGradesView: (state: IGradesState, termId: number) => state.grades[termId] ? state.grades[termId].view : GradesInitialIndividualState.view,
    selectGrade: (state: IGradesState, termId: number, gradeId: number) => state.grades[termId]  && state.grades[termId].grades[gradeId] ? state.grades[termId].grades[gradeId] : null,
    selectGradesLoadState: (state: IGradesState, termId: number) => state.grades[termId] ? state.grades[termId].loadState : GradesInitialIndividualState.loadState,
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class GradeReducer extends ImmerReducer<IInitialGradesState> {
    public loadByTermId(termId: number) {
        if (!this.draftState[termId]) {
            this.draftState[termId] = {...GradesInitialIndividualState};
        }

        this.draftState[termId].loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(version: Dtos.Version, category: Dtos.Category, term: Dtos.Term, subterm: Dtos.Term | null, grades: Dtos.Grade[]) {
        const termId = subterm ? subterm.id : term.id;
        if (!this.draftState[termId]) {
            this.draftState[termId] = {...GradesInitialIndividualState};
        }

        this.draftState[termId].version = version;
        this.draftState[termId].category = category;
        this.draftState[termId].term = term;
        this.draftState[termId].subterm = subterm;

        this.draftState[termId].grades = {};

        this.draftState[termId].view = [];

        for (const grade of grades) {
            this.draftState[termId].grades[grade.id] = grade;
            this.draftState[termId].view.push(grade.id);
        }

        this.draftState[termId].loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(termId: number, responseStatus: Dtos.ResponseStatus) {
        if (!this.draftState[termId]) {
            this.draftState[termId] = {...GradesInitialIndividualState};
        }

        this.draftState[termId].version = GradesInitialIndividualState.version;
        this.draftState[termId].category = GradesInitialIndividualState.category;
        this.draftState[termId].term = GradesInitialIndividualState.term;
        this.draftState[termId].subterm = GradesInitialIndividualState.subterm;
        this.draftState[termId].grades = GradesInitialIndividualState.grades;
        this.draftState[termId].view = GradesInitialIndividualState.view;
        this.draftState[termId].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear(termId: number) {
        delete this.draftState[termId];
    }

    public clearAll() {
        return {...GradesInitialState};
    }
}

export const GradesActions = createActionCreators(GradeReducer);
const reducer = createReducerFunction(GradeReducer, GradesInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const GradesApi = {
    loadByTermId: createGetRequest(
        Dtos.GetGradesByTermId,
        (termId: number) => ({
            termId
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const GradesLogic = {
    loadByTermId: createLogic({
        type: GradesActions.loadByTermId.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<IGradesState, typeof GradesActions.loadByTermId>, dispatch, done) => {
            try {
                const response = await GradesApi.loadByTermId(action.payload);

                dispatch(GradesActions.loadSuccess(
                    response.version,
                    response.category,
                    response.parentTerm ? response.parentTerm : response.term,
                    response.parentTerm ? response.term : null,
                    response.grades
                ));
            }
            catch (error) {
                dispatch(GradesActions.loadFailure(action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    GradesLogic.loadByTermId
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('grades', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/grades.ts